import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import s from './YearPicker.module.scss';
import { t } from '../../i18n';
import Icon from '../Icon/Icon';
import Dropdown from '../Dropdown/Dropdown';
import classNames from 'classnames';
import Select from 'react-select';

const YearPicker = ({
    years = [],
    selectedYear,
    setSelectedYear,
    onYearChange,
    clearable = false,
    modifiers = [],
}) => {
    const currentYear = new Date().getFullYear();
    const [startYear, setStartYear] = useState(currentYear - 5);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Ref for the dropdown element
    const dropdownRef = useRef(null);

    const handleYearSelect = (year) => {
        if (year === null) {
            setSelectedYear({ value: null });
            setStartYear(currentYear - 5);
        } else {
            setSelectedYear({ value: year });
            setStartYear(year - 5);
        }
        if (onYearChange) {
            onYearChange(year);
        }

        setIsDropdownOpen(false);
    };

    const handleScroll = (direction) => {
        if (direction === 'left') {
            setStartYear((prev) => Math.max(prev - 12, years[0]));
        } else if (direction === 'right') {
            setStartYear((prev) =>
                Math.min(prev + 12, years[years.length - 1] - 11)
            );
        }
    };

    const handleDropdownToggle = (isOpen) => {
        setIsDropdownOpen(isOpen);
    };

    const handleClickOutside = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target)
        ) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const classes = classNames(
        s.Root,
        modifiers.map((modifier) => s[`Root--${modifier}`]),
        {
            [s['Root--DropdownOpen']]: isDropdownOpen,
        }
    );

    const displayYears = years.slice(
        Math.max(years.indexOf(startYear), 0),
        Math.min(years.indexOf(startYear) + 12, years.length)
    );

    const canScrollLeft = startYear > years[0];
    const canScrollRight = startYear + 11 < years[years.length - 1];

    return (
        <div className={classes} ref={dropdownRef}>
            <Dropdown
                isOpen={isDropdownOpen}
                showBoxShadow
                modifiers={modifiers}
                title={
                    selectedYear
                        ? `${t('yearPicker.year')} ${selectedYear}`
                        : t('yearPicker.selectYear')
                }
                onToggle={handleDropdownToggle}>
                <div className={s.YearListContainer}>
                    <div className={s.YearControls}>
                        <div className={s.YearControlSpacer}>
                            {canScrollLeft && (
                                <button
                                    className={s.YearControlButton}
                                    onClick={() => handleScroll('left')}>
                                    <Icon
                                        name="SelectChevron"
                                        className={s.ChevronLeft}
                                    />
                                </button>
                            )}
                        </div>
                        <div className={s.YearSearch}>
                            <Select
                                className={s.YearSearchInput}
                                classNames={{
                                    menu: () => s.Menu,
                                    menuList: () => s.MenuList,
                                    input: () => s.SelectInput,
                                    singleValue: () => s.SelectSingleValue,
                                }}
                                components={{
                                    DropdownIndicator: () => {
                                        return (
                                            <div className={s.SearchIcon}>
                                                <Icon name="Search" />
                                            </div>
                                        );
                                    },
                                    IndicatorSeparator: () => null,
                                    Placeholder: () => null,
                                }}
                                options={years.map((year) => ({
                                    value: year,
                                    label: year,
                                }))}
                                value={
                                    selectedYear
                                        ? {
                                              value: selectedYear,
                                              label: selectedYear,
                                          }
                                        : null
                                }
                                onChange={(option) =>
                                    handleYearSelect(option.value)
                                }
                            />
                        </div>
                        <div className={s.YearControlSpacer}>
                            {canScrollRight && (
                                <button
                                    className={s.YearControlButton}
                                    onClick={() => handleScroll('right')}>
                                    <Icon
                                        name="SelectChevron"
                                        className={s.ChevronRight}
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                    {years.length > 0 ? (
                        <ul className={s.YearList}>
                            {displayYears.map((yearOption) => {
                                const isActive = selectedYear === yearOption;
                                const itemClasses = classNames(s.YearListItem, {
                                    [s['YearListItem--IsActive']]: isActive,
                                });

                                return (
                                    <li
                                        key={yearOption}
                                        className={itemClasses}
                                        onClick={() =>
                                            handleYearSelect(yearOption)
                                        }>
                                        {yearOption}
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className={s.NoResults}>
                            {t('yearPicker.noResults')}
                        </div>
                    )}
                    {clearable && selectedYear && (
                        <button
                            className={s.ClearButton}
                            onClick={() => handleYearSelect(null)}>
                            {t('yearPicker.clear')}
                        </button>
                    )}
                </div>
            </Dropdown>
        </div>
    );
};

YearPicker.propTypes = {
    years: PropTypes.array,
    selectedYear: PropTypes.number,
    setSelectedYear: PropTypes.func,
    onYearChange: PropTypes.func,
    clearable: PropTypes.bool,
};

export default YearPicker;
